"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCadShapeConvert = void 0;
const imageAssets_1 = require("../imageAssets");
var OrCadShapeConvert;
(function (OrCadShapeConvert) {
    const defaultNumPoints = 10;
    const defaultPointTransform = (p) => p;
    const defaultLineWidthForZero = 1;
    const setZeroLineWidthToConstant = (lineWidthForZero = defaultLineWidthForZero) => (lineWidth) => lineWidth === undefined || lineWidth === 0 ? lineWidthForZero : lineWidth;
    function ellipse(opts) {
        const numPoints = opts?.ellipse?.numPoints ?? defaultNumPoints;
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (ellipse) => {
            const points = [];
            const centerX = (ellipse.X1 + ellipse.X2) / 2;
            const centerY = (ellipse.Y1 + ellipse.Y2) / 2;
            const radiusX = Math.abs(ellipse.X2 - ellipse.X1) / 2;
            const radiusY = Math.abs(ellipse.Y2 - ellipse.Y1) / 2;
            for (let i = 0; i < numPoints; i++) {
                const angle = (i * Math.PI * 2) / numPoints;
                const x = centerX + radiusX * Math.cos(angle);
                const y = centerY + radiusY * Math.sin(angle);
                points.push(pointTransform([x, y]));
            }
            points.push([points[0][0], points[0][1]]);
            const polygon = {
                type: 'polygon',
                filled: ellipse.FillStyle !== 1,
                width: lineWidth(ellipse.LineWidth),
                points: points,
            };
            return [polygon];
        };
    }
    OrCadShapeConvert.ellipse = ellipse;
    function rect(opts) {
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (rectangle) => {
            const rect = {
                type: 'rect',
                start: pointTransform([rectangle.X1, rectangle.Y1]),
                end: pointTransform([rectangle.X2, rectangle.Y2]),
                strokeWidth: lineWidth(rectangle.LineWidth),
                filled: rectangle.FillStyle !== 1,
            };
            return [rect];
        };
    }
    OrCadShapeConvert.rect = rect;
    function line(opts) {
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const overrideWidth = opts?.line?.overrideWidth;
        const lineWidth = overrideWidth !== undefined ?
            (_) => overrideWidth
            : setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (line) => {
            const segment = {
                type: 'segment',
                start: pointTransform([line.X1, line.Y1]),
                end: pointTransform([line.X2, line.Y2]),
                width: lineWidth(line.LineWidth),
            };
            return [segment];
        };
    }
    OrCadShapeConvert.line = line;
    function polyLine(opts) {
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (polyline) => {
            const points = polyline.PolylinePoint.map((p) => pointTransform([p.X, p.Y]));
            return [
                {
                    type: 'polygon',
                    filled: false,
                    width: lineWidth(polyline.LineWidth),
                    points: points,
                },
            ];
        };
    }
    OrCadShapeConvert.polyLine = polyLine;
    function polygon(opts) {
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (polygon) => {
            const points = polygon.PolygonPoint.map((p) => pointTransform([p.X, p.Y]));
            const poly = {
                type: 'polygon',
                filled: polygon.FillStyle !== 1,
                width: lineWidth(polygon.LineWidth),
                points: points,
            };
            return [poly];
        };
    }
    OrCadShapeConvert.polygon = polygon;
    function arc(opts) {
        const numPoints = opts?.arc?.numPoints ?? defaultNumPoints;
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (arc) => {
            const cx = (arc.X1 + arc.X2) / 2;
            const cy = (arc.Y1 + arc.Y2) / 2;
            const rx = Math.abs(arc.X2 - arc.X1) / 2;
            const ry = Math.abs(arc.Y2 - arc.Y1) / 2;
            if (rx === 0 || ry === 0) {
                return [
                    {
                        type: 'segment',
                        start: pointTransform([arc.StartX, arc.StartY]),
                        end: pointTransform([arc.EndX, arc.EndY]),
                        width: lineWidth(arc.LineWidth),
                    },
                ];
            }
            const startT = Math.atan2((arc.StartY - cy) / ry, (arc.StartX - cx) / rx);
            const endT = Math.atan2((arc.EndY - cy) / ry, (arc.EndX - cx) / rx);
            const twoPi = Math.PI * 2;
            let diff = endT - startT;
            while (diff <= -Math.PI) {
                diff += twoPi;
            }
            while (diff > Math.PI) {
                diff -= twoPi;
            }
            const points = [];
            for (let i = 0; i <= numPoints; i++) {
                const t = startT + (diff * i) / numPoints;
                const x = cx + rx * Math.cos(t);
                const y = cy + ry * Math.sin(t);
                points.push(pointTransform([x, y]));
            }
            return [
                {
                    type: 'polygon',
                    filled: false,
                    width: lineWidth(arc.LineWidth),
                    points,
                },
            ];
        };
    }
    OrCadShapeConvert.arc = arc;
    function elementWithGeometries(opts) {
        return (element) => {
            const drawings = [];
            element.Ellipse?.forEach((e) => drawings.push(...ellipse(opts)(e)));
            element.Rectangle?.forEach((r) => drawings.push(...rect(opts)(r)));
            element.Line?.forEach((l) => drawings.push(...line(opts)(l)));
            element.Polyline?.forEach((p) => drawings.push(...polyLine(opts)(p)));
            element.Polygon?.forEach((p) => drawings.push(...polygon(opts)(p)));
            element.Arc?.forEach((a) => drawings.push(...arc(opts)(a)));
            return drawings;
        };
    }
    OrCadShapeConvert.elementWithGeometries = elementWithGeometries;
    function graphicLineInst(opts) {
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (line) => {
            const segment = {
                type: 'segment',
                start: pointTransform([line.StartX, line.StartY]),
                end: pointTransform([line.EndX, line.EndY]),
                width: lineWidth(line.LineWidth),
            };
            return [segment];
        };
    }
    OrCadShapeConvert.graphicLineInst = graphicLineInst;
    function graphicBoxInst(opts) {
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (box) => {
            const rect = {
                type: 'rect',
                start: pointTransform([box.X1, box.Y1]),
                end: pointTransform([box.X2, box.Y2]),
                strokeWidth: lineWidth(box.LineWidth),
                filled: box.FillStyle !== 1,
            };
            return [rect];
        };
    }
    OrCadShapeConvert.graphicBoxInst = graphicBoxInst;
    function graphicPolylineInst(opts) {
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (polyline) => {
            const points = polyline.PolylinePoint.map((p) => pointTransform([p.X, p.Y]));
            return [
                {
                    type: 'polygon',
                    filled: false,
                    width: lineWidth(polyline.LineWidth),
                    points: points,
                },
            ];
        };
    }
    OrCadShapeConvert.graphicPolylineInst = graphicPolylineInst;
    function graphicPolygonInst(opts) {
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        const lineWidth = setZeroLineWidthToConstant(opts?.zeroLineWidthOverride);
        return (polygon) => {
            const points = polygon.PolygonPoint.map((p) => pointTransform([p.X, p.Y]));
            const poly = {
                type: 'polygon',
                filled: polygon.FillStyle !== 1,
                width: lineWidth(polygon.LineWidth),
                points: points,
            };
            return [poly];
        };
    }
    OrCadShapeConvert.graphicPolygonInst = graphicPolygonInst;
    function elementBitmaps(opts) {
        const pointTransform = opts?.pointTransform ?? defaultPointTransform;
        return (element) => {
            const drawings = [];
            const imageExtractions = [];
            for (const bitmap of element.Bitmap ?? []) {
                if (!bitmap.Value) {
                    continue;
                }
                const { data, mimeType } = (0, imageAssets_1.decodeBase64Image)(bitmap.Value);
                const ref = (0, imageAssets_1.computeImageRef)(`symbol-bitmap-${bitmap.Value.substring(0, 64)}`, mimeType);
                const pos = pointTransform([bitmap.LocX, bitmap.LocY]);
                const corner = pointTransform([
                    bitmap.LocX + Math.abs(bitmap.X2 - bitmap.X1),
                    bitmap.LocY + Math.abs(bitmap.Y2 - bitmap.Y1),
                ]);
                const width = Math.abs(corner[0] - pos[0]);
                const height = Math.abs(corner[1] - pos[1]);
                drawings.push({
                    type: 'image',
                    pos: [Math.min(pos[0], corner[0]), Math.min(pos[1], corner[1])],
                    width,
                    height,
                    imageRef: ref,
                });
                imageExtractions.push({ ref, name: `symbol-bitmap`, data, mimeType });
            }
            return { drawings, imageExtractions };
        };
    }
    OrCadShapeConvert.elementBitmaps = elementBitmaps;
})(OrCadShapeConvert || (exports.OrCadShapeConvert = OrCadShapeConvert = {}));
//# sourceMappingURL=orcadShapeConverters.js.map